import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { ExternalLink, RefreshCw, Cpu, TrendingUp } from "lucide-react";

interface NewsItem {
  title: string;
  link: string;
  source: string;
  pubDate: string;
  summary: string;
  image: string;
}

function formatRelativeTime(dateStr: string): string {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    const diffMs = Date.now() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    return `${diffDay}d ago`;
  } catch {
    return "";
  }
}

/** Source-specific abbreviation for the placeholder badge */
function sourceInitials(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 3);
}

interface NewsFeedPanelProps {
  type: "tech" | "business";
}

export function NewsFeedPanel({ type }: NewsFeedPanelProps) {
  const isTech = type === "tech";
  const [imgErrors, setImgErrors] = useState<Record<number, boolean>>({});

  // Three-times-daily: stale after 8 hours, no automatic background refetch
  const techQuery = trpc.newsFeed.getTechAI.useQuery(undefined, {
    enabled: isTech,
    staleTime: 8 * 60 * 60 * 1000,
    refetchInterval: false,
  });

  const bizQuery = trpc.newsFeed.getBusiness.useQuery(undefined, {
    enabled: !isTech,
    staleTime: 8 * 60 * 60 * 1000,
    refetchInterval: false,
  });

  const query = isTech ? techQuery : bizQuery;
  const items: NewsItem[] = (query.data as NewsItem[]) ?? [];

  const title = isTech ? "Tech & AI News" : "Business News";
  const Icon = isTech ? Cpu : TrendingUp;
  const accentColor = isTech
    ? "text-[oklch(0.6_0.18_250)]"
    : "text-[oklch(0.55_0.14_145)]";
  const badgeColor = isTech
    ? "bg-[oklch(0.6_0.18_250/0.12)] text-[oklch(0.6_0.18_250)]"
    : "bg-[oklch(0.55_0.14_145/0.12)] text-[oklch(0.55_0.14_145)]";
  const placeholderBg = isTech
    ? "bg-[oklch(0.92_0.04_250)]"
    : "bg-[oklch(0.92_0.04_145)]";
  const placeholderText = isTech
    ? "text-[oklch(0.5_0.12_250)]"
    : "text-[oklch(0.45_0.1_145)]";

  return (
    <div className="flex flex-col h-full bg-[oklch(0.98_0.005_240)] border border-[oklch(0.88_0.01_240)] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[oklch(0.88_0.01_240)] bg-white">
        <div className="flex items-center gap-2">
          <Icon size={15} className={accentColor} />
          <span className="text-xs font-semibold uppercase tracking-widest text-[oklch(0.35_0.02_240)]">
            {title}
          </span>
        </div>
        <button
          onClick={() => query.refetch()}
          disabled={query.isFetching}
          className="p-1 rounded hover:bg-[oklch(0.93_0.01_240)] transition-colors disabled:opacity-40"
          title="Refresh"
        >
          <RefreshCw
            size={13}
            className={`text-[oklch(0.5_0.02_240)] ${query.isFetching ? "animate-spin" : ""}`}
          />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto divide-y divide-[oklch(0.92_0.005_240)]">
        {query.isLoading && (
          <div className="flex items-center justify-center h-32 text-xs text-[oklch(0.55_0.02_240)]">
            Loading news…
          </div>
        )}
        {query.isError && (
          <div className="flex items-center justify-center h-32 text-xs text-red-500">
            Failed to load news. Click refresh to retry.
          </div>
        )}
        {!query.isLoading && items.length === 0 && !query.isError && (
          <div className="flex items-center justify-center h-32 text-xs text-[oklch(0.55_0.02_240)]">
            No articles available right now.
          </div>
        )}
        {items.map((item, i) => {
          const hasImage = item.image && !imgErrors[i];
          return (
            <a
              key={i}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-3 px-4 py-3 hover:bg-white transition-colors group"
            >
              {/* Thumbnail */}
              <div className={`shrink-0 w-16 h-16 rounded-lg overflow-hidden ${!hasImage ? `${placeholderBg} flex items-center justify-center` : ""}`}>
                {hasImage ? (
                  <img
                    src={item.image}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={() => setImgErrors((prev) => ({ ...prev, [i]: true }))}
                  />
                ) : (
                  <span className={`text-[10px] font-bold ${placeholderText}`}>
                    {sourceInitials(item.source)}
                  </span>
                )}
              </div>

              {/* Text content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span
                    className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${badgeColor} shrink-0`}
                  >
                    {item.source}
                  </span>
                  <span className="text-[10px] text-[oklch(0.6_0.01_240)] shrink-0">
                    {formatRelativeTime(item.pubDate)}
                  </span>
                </div>

                {/* Headline */}
                <p className="text-[18px] font-semibold text-[oklch(0.18_0.02_240)] leading-snug group-hover:text-[oklch(0.3_0.12_250)] transition-colors line-clamp-2">
                  {item.title}
                  <ExternalLink
                    size={10}
                    className="inline ml-1 text-[oklch(0.7_0.01_240)] opacity-0 group-hover:opacity-100 transition-opacity align-middle"
                  />
                </p>

                {/* Summary */}
                {item.summary && (
                  <p className="text-[15px] text-[oklch(0.52_0.01_240)] mt-0.5 line-clamp-2 leading-relaxed">
                    {item.summary}
                  </p>
                )}
              </div>
            </a>
          );
        })}
      </div>

      {/* Footer */}
      {items.length > 0 && (
        <div className="px-4 py-2 border-t border-[oklch(0.88_0.01_240)] bg-white">
          <p className="text-[10px] text-[oklch(0.6_0.01_240)]">
            {items.length} articles · Updates 3× daily · 7am, 11am, 4:30pm
          </p>
        </div>
      )}
    </div>
  );
}
